<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div :id="mapId" :small="small" class="Map" ref="mapContainer">
    <slot></slot>
  </div>
</template>
<script setup lang="ts">
const emit = defineEmits<{
  mapReady: [map: maplibregl.Map]
  /** Style du fond remplacé (choix utilisateur ou bascule en ligne / hors ligne). */
  basemapChanged: [map: maplibregl.Map]
}>()

import cameroonMask from '@/assets/geojsons/mask_cameroun.json'
import { isOfflineBasemap, resolveEffectiveBasemap } from '@/config/basemaps'
import { createCameroonLngLatBounds, createCameroonMaxPanBounds } from '@/config/cameroonMapBounds'
import { useConnectivity } from '@/composables/useConnectivity'
import { martinSpriteUrl } from '@/config/env'
import type Basemap from '@/models/interfaces/map/Basemap'
import MapService from '@/services/map/MapService'
import { transformOfflineGlyphRequest } from '@/services/map/OfflineBasemapStyle'
import { bbox as turfBbox } from '@turf/turf'
import { getCommuneViewDataFromGeoJSON } from '@/data/communesCameroonGeoJSON'
import {
  buildCommuneMaskGeoJSONFromGeometry,
  getCommuneBoundsFromMap,
  type CommuneViewData,
} from '@/services/map/CommuneViewBoundsService'
import { CommuneService } from '@/services/commune/CommuneService'
import { CommuneLayerService } from '@/services/map/CommuneLayerService'
import { debounce } from '@/services/utils/UtilsService'
import { useApplicationStore } from '@/stores/applicationStore'
import { useMyMapStore } from '@/stores/myMapStore'
import maplibregl, { GeoJSONSource } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  type Ref,
  watch,
} from 'vue'

const applicationStore = useApplicationStore()
const myMapStore = useMyMapStore()
const triggerZoomReset = computed(() => applicationStore.triggerZoomReset)
const map = shallowRef<maplibregl.Map | null>(null)
const mapContainer = ref<HTMLElement | null>(null)
const props = withDefaults(
  defineProps<{
    bounds?: maplibregl.LngLatBounds
    small?: boolean
    mapId?: string
    /** Fond choisi (sélecteur) ; absent : fond par défaut. Remplacé par le fond local hors ligne. */
    basemap?: Basemap | null
  }>(),
  {
    mapId: 'map',
    basemap: null,
    bounds: () => createCameroonLngLatBounds(),
    small: false,
  },
)
const COMMUNE_MASK_SOURCE_ID = 'communeMask'
const COMMUNE_MASK_LAYER_ID = 'communeMask'
const CAMEROON_MASK_LAYER_ID = 'cameroonMask'
const DEFAULT_MIN_ZOOM = 0
const VIEW_PADDING = 10

const popup = shallowRef(new maplibregl.Popup({ closeOnClick: false }))
const hoveredFeatureId: Ref<string | null> = ref(null)
const activeFeatureId: Ref<string | null> = ref(null)

const { isOnline, ready: connectivityReady } = useConnectivity()
/** Fond affiché : celui demandé en ligne, le fond local quand internet ne répond plus. */
const effectiveBasemap = computed(() => resolveEffectiveBasemap(props.basemap, isOnline.value))

async function buildStyleWithMakiSprite(
  basemap: Basemap,
): Promise<string | maplibregl.StyleSpecification> {
  if (isOfflineBasemap(basemap)) return MapService.loadStyle(basemap)
  const styleUrl = MapService.getStyle(basemap)
  const isMapboxStyle = typeof styleUrl === 'string' && styleUrl.includes('api.mapbox.com')

  if (isMapboxStyle) {
    try {
      const response = await fetch(styleUrl)
      if (!response.ok) throw new Error(`Style fetch ${response.status}`)
      const styleJson = MapService.resolveMapboxStyleUrls(
        (await response.json()) as maplibregl.StyleSpecification,
      )
      if (martinSpriteUrl) {
        if (typeof styleJson.sprite === 'string') {
          styleJson.sprite = [
            { id: 'default', url: styleJson.sprite },
            { id: 'maki', url: martinSpriteUrl },
          ]
        } else if (!styleJson.sprite) {
          styleJson.sprite = martinSpriteUrl
        }
      }
      return styleJson
    } catch {
      return styleUrl as string
    }
  }

  return styleUrl as string
}

/** Register before style/sprite load so missing sprite icons do not spam the console. */
function attachStyleImageMissingHandler(m: maplibregl.Map): void {
  m.on('styleimagemissing', (e: { id?: string | null }) => {
    const id = e.id
    if (id == null || id === '' || id === 'null' || m.hasImage(id)) return
    const size = 2
    const data = new Uint8ClampedArray(size * size * 4)
    const placeholder = new ImageData(data, size, size)
    try {
      m.addImage(id, placeholder, { pixelRatio: 1 })
    } catch (err) {
      if (err instanceof Error && !err.message.includes('already exists')) {
        console.warn('[Map] styleimagemissing: could not add placeholder for', id, err)
      }
    }
  })
}

onMounted(async () => {
  await nextTick()
  if (!mapContainer.value) {
    console.error('Map container element not found')
    return
  }
  // Premier sondage de connexion avant de choisir le fond : sans internet, on démarre directement
  // sur le fond local au lieu d'afficher une carte vide.
  await connectivityReady()
  const initialBasemap = effectiveBasemap.value
  const style = await buildStyleWithMakiSprite(initialBasemap)
  currentBasemapId = initialBasemap.id
  if (!mapContainer.value) return
  map.value = new maplibregl.Map({
    container: mapContainer.value,
    style: style as maplibregl.StyleSpecification | string,
    bounds: createCameroonLngLatBounds(),
    fitBoundsOptions: { padding: VIEW_PADDING },
    attributionControl: false,
    maxBounds: createCameroonMaxPanBounds(),
    transformRequest: transformOfflineGlyphRequest,
  })

  attachStyleImageMissingHandler(map.value)

  map.value.dragRotate.disable()
  map.value.touchZoomRotate.disableRotation()

  map.value.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right')

  map.value.on('error', (e) => {
    if (e.error && 'status' in e.error && (e.error as { status?: number }).status === 404) return
  })

  // Lock drag panning when at minZoom (full-country view): the viewport aspect ratio
  // may not match maxBounds, leaving horizontal room to pan within the bounds.
  // Panning is re-enabled once the user zooms in past the country level.
  const DRAG_PAN_UNLOCK_DELTA = 0.5
  const syncDragPan = () => {
    if (!map.value) return
    const atCountryLevel = map.value.getZoom() <= map.value.getMinZoom() + DRAG_PAN_UNLOCK_DELTA
    if (atCountryLevel) {
      map.value.dragPan.disable()
    } else {
      map.value.dragPan.enable()
    }
  }
  map.value.on('zoomend', syncDragPan)
  map.value.once('load', syncDragPan)

  // Re-frame the active commune view when the container is resized (e.g. a fiche/side
  // panel opening or closing), so the commune stays centered instead of drifting off
  // the visible area once the panel closes.
  map.value.on('resize', () => debouncedRefitActiveCommuneView())

  setBBox()
  map.value.on('load', () => {
    addSource('cameroonMask', cameroonMask as GeoJSON.GeoJSON)
    map.value?.addLayer({
      id: 'cameroonMask',
      type: 'fill',
      source: 'cameroonMask',
      paint: {
        'fill-color': '#000000',
        'fill-opacity': 0.7,
      },
      metadata: { isPersistent: true, layerGroup: 'fond' },
    })
    emit('mapReady', map.value!)
  })
})

/** Fond réellement chargé dans la carte (évite de recharger le même style). */
let currentBasemapId: string | null = null

// Changement de fond demandé par l'utilisateur, ou bascule en ligne / hors ligne.
watch(effectiveBasemap, (basemap) => {
  const m = map.value
  if (!m || basemap.id === currentBasemapId) return
  currentBasemapId = basemap.id
  MapService.updateStyle(m, basemap)
    .then(() => emit('basemapChanged', m))
    .catch(() => {
      // Style injoignable : on retentera au prochain changement.
      if (currentBasemapId === basemap.id) currentBasemapId = null
    })
})

const removeSource = (sourceName: string) => {
  if (map.value?.getSource(sourceName)) {
    map.value.removeSource(sourceName)
  }
}

const removeLayer = (layerName: string) => {
  if (map.value && map.value?.getLayer(layerName)) {
    map.value.removeLayer(layerName)
  }
}

const setData = (sourceName: string, geojson: GeoJSON.GeoJSON) => {
  const source = map.value?.getSource(sourceName.toString()) as GeoJSONSource
  if (source) source.setData(geojson)
}

const getData = async (sourceName: string | number) => {
  const source = map.value?.getSource(sourceName.toString()) as GeoJSONSource
  if (source) return await source.getData()
}

const addSource = (
  sourceName: string,
  geojson: GeoJSON.GeoJSON,
  options?: {
    promoteId?: string
    cluster?: boolean
    clusterMaxZoom?: number
    clusterRadius?: number
  },
) => {
  if (map.value?.getSource(sourceName)) return
  map.value?.addSource(sourceName, {
    type: 'geojson',
    data: geojson,
    ...(options?.promoteId ? { promoteId: options.promoteId } : {}),
    ...(options?.cluster ? { cluster: true } : {}),
    ...(options?.clusterMaxZoom != null ? { clusterMaxZoom: options.clusterMaxZoom } : {}),
    ...(options?.clusterRadius != null ? { clusterRadius: options.clusterRadius } : {}),
  })
}

const addVectorTileSource = (
  sourceName: string,
  tilesUrl: string,
  minZoom: number = 0,
  maxZoom: number = 22,
  promoteId?: string,
) => {
  if (map.value?.getSource(sourceName)) return
  const sourceConfig: maplibregl.VectorSourceSpecification = {
    type: 'vector',
    tiles: [tilesUrl],
    minzoom: minZoom,
    maxzoom: maxZoom,
  }
  if (promoteId) {
    sourceConfig.promoteId = promoteId
  }
  map.value?.addSource(sourceName, sourceConfig)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const setLayoutProperty = (layerName: string, property: string, value: any) => {
  if (map.value?.getLayer(layerName)) {
    map.value?.setLayoutProperty(layerName, property, value)
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const setPaintProperty = (layerName: string, property: string, value: any) => {
  if (map.value?.getLayer(layerName)) {
    map.value?.setPaintProperty(layerName, property, value)
  }
}

/** Invisible circle on top of the symbol: larger pointer hit area (symbol icons are tiny). */
const PLATFORM_MARKER_HIT_LAYER_SUFFIX = '-hit'
const PLATFORM_MARKER_HIT_RADIUS_PX = 44

/**
 * Feature-state driven highlight circle rendered behind the symbol icon.
 * Using setFeatureState is a GPU-side state update — no style recompilation, zero lag.
 */
const PLATFORM_MARKER_HIGHLIGHT_LAYER_SUFFIX = '-highlight'
const PLATFORM_MARKER_HIGHLIGHT_RADIUS_PX = 20
const PLATFORM_MARKER_ACTIVE_COLOR = '#156061'
const PLATFORM_MARKER_HOVER_COLOR = '#1a8a8b'

function platformMarkerHitLayerId(symbolLayerId: string): string {
  return `${symbolLayerId}${PLATFORM_MARKER_HIT_LAYER_SUFFIX}`
}

function platformMarkerHighlightLayerId(symbolLayerId: string): string {
  return `${symbolLayerId}${PLATFORM_MARKER_HIGHLIGHT_LAYER_SUFFIX}`
}

function resolvePlatformMarkerInteractionLayerId(symbolLayerId: string): string {
  if (map.value?.getLayer(platformMarkerHitLayerId(symbolLayerId))) {
    return platformMarkerHitLayerId(symbolLayerId)
  }
  return symbolLayerId
}

// Avoid stacking duplicate layer handlers when addPopupOnClick / listenToHoveredFeature run again.
const platformMarkerClickHandlers = new Map<string, (e: maplibregl.MapLayerMouseEvent) => void>()
const platformMarkerHoverHandlers = new Map<
  string,
  {
    onEnter: (e: maplibregl.MapLayerMouseEvent) => void
    onLeave: () => void
  }
>()

/** Source + feature id of the currently active (clicked) marker, for cleanup on popup close. */
let activeMarkerState: { source: string; id: string } | null = null

function clearActiveMarkerState(): void {
  if (activeMarkerState != null && map.value != null) {
    map.value.setFeatureState(
      { source: activeMarkerState.source, id: activeMarkerState.id },
      { active: false },
    )
  }
  activeMarkerState = null
}

const addLayer = async (
  layerName: string,
  options: {
    layout: maplibregl.LayerSpecification['layout']
    /** Extra filter AND-combined with geometry filter on all three sub-layers. */
    filter?: maplibregl.ExpressionSpecification
  },
) => {
  if (map.value && map.value?.getLayer(layerName)) return

  // Base geometry filter — restrict to point features only.
  const pointFilter: maplibregl.ExpressionSpecification = ['in', ['geometry-type'], ['literal', ['Point', 'MultiPoint']]]
  // When a caller passes an additional filter (e.g. unclustered-only), AND it in.
  const resolvedFilter: maplibregl.ExpressionSpecification = options.filter
    ? (['all', pointFilter, options.filter] as maplibregl.ExpressionSpecification)
    : pointFilter

  // Highlight circle rendered behind the icon.
  // Color and opacity are driven by feature-state (`hover`, `active`) — GPU-side, no recompilation.
  map.value?.addLayer({
    id: platformMarkerHighlightLayerId(layerName),
    type: 'circle',
    source: layerName,
    filter: resolvedFilter,
    paint: {
      'circle-radius': PLATFORM_MARKER_HIGHLIGHT_RADIUS_PX,
      'circle-color': [
        'case',
        ['boolean', ['feature-state', 'active'], false],
        PLATFORM_MARKER_ACTIVE_COLOR,
        PLATFORM_MARKER_HOVER_COLOR,
      ],
      'circle-opacity': [
        'case',
        ['boolean', ['feature-state', 'active'], false],
        0.85,
        ['boolean', ['feature-state', 'hover'], false],
        0.4,
        0,
      ],
      'circle-stroke-width': 0,
    },
    metadata: { isPersistent: true, layerGroup: 'indicator' },
  })

  map.value?.addLayer({
    id: layerName,
    type: 'symbol',
    source: layerName,
    filter: resolvedFilter,
    layout: options.layout,
    metadata: { isPersistent: true, layerGroup: 'indicator' },
  })
  map.value?.addLayer({
    id: platformMarkerHitLayerId(layerName),
    type: 'circle',
    source: layerName,
    filter: resolvedFilter,
    paint: {
      'circle-radius': PLATFORM_MARKER_HIT_RADIUS_PX,
      'circle-color': '#000000',
      'circle-opacity': 0,
      'circle-stroke-width': 0,
    },
    metadata: { isPersistent: true, layerGroup: 'indicator' },
  })
}

const addImage = async (path: string, name: string) => {
  if (map.value?.hasImage(name)) return
  const image = await map.value?.loadImage(path)
  if (!image) return
  if (map.value?.hasImage(name)) return
  try {
    map.value?.addImage(name, image.data)
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    if (!errorMessage.includes('already exists')) {
      console.error(`Error adding image "${name}":`, error)
    }
  }
}

const addPopup = debounce(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (coordinates: maplibregl.LngLatLike | undefined, popupHtml: any, isComponent = true) => {
    if (map.value == null || coordinates == null) {
      console.warn('[Map] addPopup skipped', {
        mapMissing: map.value == null,
        coordinatesMissing: coordinates == null,
        coordinates,
      })
      return
    }

    const domContent = isComponent ? popupHtml.$el : popupHtml
    if (domContent == null) {
      console.warn('[Map] addPopup skipped: no DOM content', { isComponent, popupHtml })
      return
    }

    console.log('[Map] addPopup', { coordinates, activeFeatureId: activeFeatureId.value })
    popup.value.setLngLat(coordinates).setDOMContent(domContent).addTo(map.value)

    popup.value.addClassName('show')
    flyTo(coordinates)
    map.value.once('moveend', ensurePopupInView)
    popup.value._onClose = () => {
      activeFeatureId.value = null
      popup.value.removeClassName('show')
      clearActiveMarkerState()
    }
  },
  100,
)

const ensurePopupInView = () => {
  if (map.value == null || popup.value == null) return
  const popupAnchor = popup.value.getLngLat()
  const mapCanvas = map.value.getCanvas()
  const mapRect = mapCanvas.getBoundingClientRect()
  const popupElement = popup.value._container

  if (!popupAnchor || !popupElement) return

  const popupRect = popupElement.getBoundingClientRect()
  const offset = 60
  // Calculate overflow in pixels
  const overflow = {
    top: mapRect.top - popupRect.top + offset,
    bottom: popupRect.bottom - mapRect.bottom + offset,
    left: mapRect.left - popupRect.left + offset,
    right: popupRect.right - mapRect.right + offset,
  }

  // Only move if overflow happens
  let dx = 0,
    dy = 0
  if (overflow.left > 0) dx = -overflow.left
  else if (overflow.right > 0) dx = overflow.right

  if (overflow.top > 0) dy = -overflow.top
  else if (overflow.bottom > 0) dy = overflow.bottom

  if (dx !== 0 || dy !== 0) {
    map.value.panBy([dx, dy], { duration: 300 })
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const addPopupOnClick = (layerName: string, popupHtml: any, isComponent = true) => {
  if (map.value == null || popupHtml == null) {
    console.warn('[Map] addPopupOnClick skipped', {
      layerName,
      mapMissing: map.value == null,
      popupHtmlMissing: popupHtml == null,
    })
    return
  }
  const interactionLayerId = resolvePlatformMarkerInteractionLayerId(layerName)
  const previousClick = platformMarkerClickHandlers.get(interactionLayerId)
  if (previousClick) {
    map.value.off('click', interactionLayerId, previousClick)
  }
  const onLayerClick = (e: maplibregl.MapLayerMouseEvent) => {
    console.log('[Map] CLICK', {
      layerName,
      interactionLayerId,
      featureCount: e.features?.length ?? 0,
    })
    if (map.value == null) return
    const feature = e.features?.[0]
    if (!feature) {
      console.log('[Map] layer click: no feature', { layerName, interactionLayerId })
      return
    }
    const rawId = feature.properties?.id
    const geom = feature.geometry
    const coordinates: maplibregl.LngLatLike | undefined =
      geom != null && geom.type === 'Point' && Array.isArray(geom.coordinates)
        ? ([geom.coordinates[0], geom.coordinates[1]] as maplibregl.LngLatLike)
        : undefined
    console.log('[Map] layer click', {
      layerName,
      interactionLayerId,
      rawId,
      propertyKeys: feature.properties != null ? Object.keys(feature.properties) : [],
      coordinates,
    })
    // Clear the previous active state, then mark the clicked feature as active.
    clearActiveMarkerState()
    activeFeatureId.value = rawId != null && rawId !== '' ? String(rawId) : null
    if (activeFeatureId.value == null) {
      console.warn('[Map] layer click: feature has no usable id in properties.id', {
        layerName,
        properties: feature.properties,
      })
    } else {
      activeMarkerState = { source: layerName, id: activeFeatureId.value }
      map.value.setFeatureState({ source: layerName, id: activeFeatureId.value }, { active: true })
    }
    addPopup(coordinates, popupHtml, isComponent)
    e.preventDefault()
  }
  platformMarkerClickHandlers.set(interactionLayerId, onLayerClick)
  map.value.on('click', interactionLayerId, onLayerClick)
}

const flyTo = (coordinates: maplibregl.LngLatLike, speed = 0.5) => {
  if (map.value == null) return
  map.value.flyTo({
    center: coordinates,
    zoom: map.value.getZoom() > 7 ? map.value.getZoom() : 7,
    speed,
  })
}

function fitToDefaultBounds(): void {
  if (map.value == null) return
  map.value.fitBounds(props.bounds, { padding: VIEW_PADDING, duration: 800 })
}

function closeActivePopup(): void {
  activeFeatureId.value = null
  clearActiveMarkerState()
  popup.value.remove()
  popup.value.removeClassName('show')
}

const listenToHoveredFeature = (layerName: string) => {
  if (map.value == null) return
  const interactionLayerId = resolvePlatformMarkerInteractionLayerId(layerName)
  const previousHover = platformMarkerHoverHandlers.get(interactionLayerId)
  if (previousHover) {
    map.value.off('mouseenter', interactionLayerId, previousHover.onEnter)
    map.value.off('mouseleave', interactionLayerId, previousHover.onLeave)
  }
  // Track the currently hovered feature id locally so onLeave can clear it precisely.
  let currentHoveredId: string | null = null
  const onEnter = (e: maplibregl.MapLayerMouseEvent) => {
    if (map.value == null) return
    const feature = e.features?.[0]
    if (!feature) return
    map.value.getCanvas().style.cursor = 'pointer'
    const featureId = String(feature.properties?.id ?? '')
    if (!featureId) return
    hoveredFeatureId.value = featureId
    currentHoveredId = featureId
    map.value.setFeatureState({ source: layerName, id: featureId }, { hover: true })
  }
  const onLeave = () => {
    if (map.value == null) return
    map.value.getCanvas().style.cursor = ''
    hoveredFeatureId.value = null
    if (currentHoveredId != null) {
      map.value.setFeatureState({ source: layerName, id: currentHoveredId }, { hover: false })
      currentHoveredId = null
    }
  }
  platformMarkerHoverHandlers.set(interactionLayerId, { onEnter, onLeave })
  map.value.on('mouseenter', interactionLayerId, onEnter)
  map.value.on('mouseleave', interactionLayerId, onLeave)
}

const setBBox = () => {
  if (!map.value) return
  map.value.fitBounds(props.bounds, { padding: VIEW_PADDING })
}

/**
 * Bounds of the commune currently framed on the map. Kept around so refitActiveCommuneView
 * can re-fit the same commune after the container is resized (e.g. a fiche/side panel
 * opening or closing) instead of leaving the camera at its old center/zoom.
 */
const activeCommuneFitBounds = shallowRef<[[number, number], [number, number]] | null>(null)

function applyCommuneView(
  communeBounds: maplibregl.LngLatBounds,
  communeGeometry: GeoJSON.Polygon | GeoJSON.MultiPolygon,
) {
  if (!map.value) return
  removeLayer(COMMUNE_MASK_LAYER_ID)
  removeSource(COMMUNE_MASK_SOURCE_ID)
  const maskGeoJSON = buildCommuneMaskGeoJSONFromGeometry(communeGeometry)
  addSource(COMMUNE_MASK_SOURCE_ID, maskGeoJSON as GeoJSON.GeoJSON)
  map.value.addLayer({
    id: COMMUNE_MASK_LAYER_ID,
    type: 'fill',
    source: COMMUNE_MASK_SOURCE_ID,
    paint: {
      'fill-color': '#000000',
      'fill-opacity': 0.7,
    },
    metadata: { isPersistent: true, layerGroup: 'fond' },
  })
  setLayoutProperty(CAMEROON_MASK_LAYER_ID, 'visibility', 'none')
  const [minLng, minLat, maxLng, maxLat] = turfBbox(communeGeometry)
  const communeBoundsForFit: [[number, number], [number, number]] = [
    [minLng, minLat],
    [maxLng, maxLat],
  ]
  activeCommuneFitBounds.value = communeBoundsForFit

  map.value.boxZoom.disable()
  map.value.dragPan.disable()
  map.value.scrollZoom.disable()
  map.value.doubleClickZoom.disable()
  map.value.touchZoomRotate.disable()

  function reenableMapInteraction() {
    if (!map.value) return
    const currentBounds = map.value.getBounds()
    map.value.setMaxBounds(currentBounds)
    map.value.boxZoom.enable()
    map.value.dragPan.enable()
    map.value.scrollZoom.enable()
    map.value.doubleClickZoom.enable()
    map.value.touchZoomRotate.enable()
  }

  map.value.once('moveend', reenableMapInteraction)

  const FLY_DURATION_MS = 800
  map.value.fitBounds(communeBoundsForFit, {
    padding: 20,
    duration: FLY_DURATION_MS,
  })

  setTimeout(reenableMapInteraction, FLY_DURATION_MS + 50)
}

/**
 * Re-frames the currently active commune view after the map container has been resized
 * (e.g. a fiche/side panel opening or closing changes the container's width). Without this,
 * the camera keeps its old center/zoom while only the pan-lock bounds move with it, so the
 * commune ends up off-center or zoomed past the visible area.
 *
 * The pan-lock (maxBounds) is lifted before re-fitting and recomputed from the fresh
 * getBounds() afterwards, so it always matches the *current* container aspect ratio — a
 * bounds box derived from the commune geometry itself would keep the commune's own (often
 * portrait) aspect ratio, which fights a landscape container and forces the camera to
 * over-zoom to satisfy both at once, cutting the commune off top and bottom.
 */
function refitActiveCommuneView() {
  if (!map.value || !activeCommuneFitBounds.value) return
  map.value.setMaxBounds(createCameroonMaxPanBounds())
  map.value.fitBounds(activeCommuneFitBounds.value, { padding: 20, duration: 0 })
  map.value.setMaxBounds(map.value.getBounds())
}
const debouncedRefitActiveCommuneView = debounce(refitActiveCommuneView, 200)

function clearCommuneView() {
  if (!map.value) return
  activeCommuneFitBounds.value = null
  removeLayer(COMMUNE_MASK_LAYER_ID)
  removeSource(COMMUNE_MASK_SOURCE_ID)
  setLayoutProperty(CAMEROON_MASK_LAYER_ID, 'visibility', 'visible')
  map.value.setMaxBounds(createCameroonMaxPanBounds())
  map.value.setMinZoom(DEFAULT_MIN_ZOOM)
  map.value.boxZoom.enable()
  map.value.dragPan.enable()
  map.value.scrollZoom.enable()
  map.value.doubleClickZoom.enable()
  map.value.touchZoomRotate.enable()
  map.value.fitBounds(props.bounds, { padding: VIEW_PADDING })
  myMapStore.communeViewData = null
  myMapStore.lockedCommuneCode = null
}

function runApplyCommuneViewIfNeeded() {
  const mapSearchValue = myMapStore.mapSearch
  if (mapSearchValue === null) {
    clearCommuneView()
    return
  }
  const communeName = mapSearchValue.name
  if (!communeName || !map.value) return

  const dataFromClick = myMapStore.communeViewData
  if (dataFromClick && dataFromClick.name === communeName) {
    applyWhenReady(dataFromClick.bounds, dataFromClick.geometry)
    return
  }

  function applyWhenReady(
    bounds: maplibregl.LngLatBounds,
    geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon,
  ) {
    if (!map.value) return
    if (!map.value.loaded()) {
      map.value.once('load', () => applyCommuneView(bounds, geometry))
    } else {
      applyCommuneView(bounds, geometry)
    }
  }

  nextTick(async () => {
    if (!map.value || myMapStore.mapSearch === null) return
    const currentSearch = myMapStore.mapSearch
    const name = currentSearch.name
    if (!name) return

    // Priority 1: local GeoJSON (357 communes, instant from cache after first load).
    let communeData: CommuneViewData | null = null
    try {
      communeData = await getCommuneViewDataFromGeoJSON(name)
    } catch (err) {
      console.error('[Map] GeoJSON lookup failed', err)
    }

    if (myMapStore.mapSearch === null || !map.value) return

    if (communeData) {
      applyWhenReady(communeData.bounds, communeData.geometry)
      return
    }

    // Priority 2: fetch geometry from API for communes missing from local GeoJSON.
    try {
      const apiCommune = await CommuneService.searchCommunes(name, 1).then((r) => r[0] ?? null)
      if (myMapStore.mapSearch === null || !map.value) return
      const geometry = apiCommune?.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon | undefined
      if (geometry && (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon')) {
        const [minLng, minLat, maxLng, maxLat] = turfBbox(geometry)
        const bounds = new maplibregl.LngLatBounds([minLng, minLat], [maxLng, maxLat])
        applyWhenReady(bounds, geometry)
        return
      }
    } catch (err) {
      console.error('[Map] API geometry fallback failed', err)
    }

    if (myMapStore.mapSearch === null || !map.value) return

    // Priority 3: fly to centroid first, then query vector tile source for geometry.
    // The commune layer must be visible so MapLibre loads tiles around the new viewport.
    const coords = currentSearch.coords
    const hasCentroid = coords && typeof coords.lng === 'number' && typeof coords.lat === 'number'

    if (hasCentroid) {
      // Make the commune layer visible so tiles are requested for the target area.
      CommuneLayerService.setCommuneLayerVisibility(myMapStore.myMap, true)

      const doFlyAndQuery = async () => {
        if (!map.value || myMapStore.mapSearch === null) return
        map.value.flyTo({ center: [coords.lng, coords.lat], zoom: 10, speed: 1 })
        // Wait for the flyTo to reach the target area, then query tiles.
        await new Promise<void>((resolve) => {
          const onMoveEnd = () => {
            map.value?.off('moveend', onMoveEnd)
            resolve()
          }
          map.value?.once('moveend', onMoveEnd)
          // Safety timeout in case moveend never fires.
          setTimeout(resolve, 3000)
        })
        if (myMapStore.mapSearch === null || !map.value) return
        const tileData = await getCommuneBoundsFromMap(map.value, name)
        if (tileData && myMapStore.mapSearch !== null && map.value) {
          applyWhenReady(tileData.bounds, tileData.geometry)
        }
      }

      if (map.value.loaded()) {
        void doFlyAndQuery()
      } else {
        map.value.once('load', () => void doFlyAndQuery())
      }
    }
  })
}

watch(() => myMapStore.mapSearch?.name ?? null, runApplyCommuneViewIfNeeded)

watch(
  map,
  (newMap) => {
    if (newMap && myMapStore.mapSearch !== null) {
      runApplyCommuneViewIfNeeded()
    }
  },
  { immediate: true },
)

watch(triggerZoomReset, () => {
  setBBox()
})

// --- User-dropped layer feature popup ---

const userLayerPopup = shallowRef(
  new maplibregl.Popup({ closeButton: true, closeOnClick: false, maxWidth: '320px' }),
)
const userLayerClickHandlers = new Map<string, (e: maplibregl.MapLayerMouseEvent) => void>()
const userLayerHoverHandlers = new Map<string, { onEnter: () => void; onLeave: () => void }>()

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function buildUserLayerPopupHtml(
  layerName: string,
  properties: Record<string, unknown> | null | undefined,
): string {
  const entries = Object.entries(properties ?? {}).filter(([, v]) => v != null && v !== '')
  const title = `<h4 class="UserLayerPopup__title">${escapeHtml(layerName)}</h4>`
  if (entries.length === 0) {
    return `<div class="UserLayerPopup">${title}<p class="UserLayerPopup__empty">Aucune propriété</p></div>`
  }
  const rows = entries
    .map(
      ([k, v]) =>
        `<tr><td class="UserLayerPopup__key">${escapeHtml(k)}</td><td class="UserLayerPopup__val">${escapeHtml(String(v))}</td></tr>`,
    )
    .join('')
  return `<div class="UserLayerPopup">${title}<table class="UserLayerPopup__table">${rows}</table></div>`
}

function registerUserLayerHandlers(layerIds: string[], layerName: string): void {
  const mapInstance = map.value
  if (!mapInstance) return
  for (const layerId of layerIds) {
    if (userLayerClickHandlers.has(layerId)) continue
    const onClick = (e: maplibregl.MapLayerMouseEvent) => {
      const feature = e.features?.[0]
      if (!feature) return
      userLayerPopup.value
        .setLngLat([e.lngLat.lng, e.lngLat.lat])
        .setHTML(buildUserLayerPopupHtml(layerName, feature.properties))
        .addTo(mapInstance)
      e.preventDefault()
    }
    const onEnter = () => {
      mapInstance.getCanvas().style.cursor = 'pointer'
    }
    const onLeave = () => {
      mapInstance.getCanvas().style.cursor = ''
    }
    mapInstance.on('click', layerId, onClick)
    mapInstance.on('mouseenter', layerId, onEnter)
    mapInstance.on('mouseleave', layerId, onLeave)
    userLayerClickHandlers.set(layerId, onClick)
    userLayerHoverHandlers.set(layerId, { onEnter, onLeave })
  }
}

function unregisterUserLayerHandlers(layerIds: string[]): void {
  const mapInstance = map.value
  if (!mapInstance) return
  for (const layerId of layerIds) {
    const onClick = userLayerClickHandlers.get(layerId)
    const onHover = userLayerHoverHandlers.get(layerId)
    if (onClick) {
      mapInstance.off('click', layerId, onClick)
      userLayerClickHandlers.delete(layerId)
    }
    if (onHover) {
      mapInstance.off('mouseenter', layerId, onHover.onEnter)
      mapInstance.off('mouseleave', layerId, onHover.onLeave)
      userLayerHoverHandlers.delete(layerId)
    }
  }
}

watch(
  () => myMapStore.userDroppedLayers,
  (newLayers, oldLayers) => {
    if (!map.value) return
    for (const layer of newLayers) {
      registerUserLayerHandlers(layer.hitLayerIds ?? layer.layerIds, layer.name)
    }
    const newIds = new Set(newLayers.map((l) => l.id))
    for (const layer of oldLayers ?? []) {
      if (!newIds.has(layer.id)) unregisterUserLayerHandlers(layer.hitLayerIds ?? layer.layerIds)
    }
  },
  { deep: false, immediate: true },
)

watch(map, (mapInstance) => {
  if (mapInstance) {
    for (const layer of myMapStore.userDroppedLayers) {
      registerUserLayerHandlers(layer.hitLayerIds ?? layer.layerIds, layer.name)
    }
  }
})

onBeforeUnmount(() => {
  userLayerPopup.value.remove()
})

defineExpose({
  map,
  activeFeatureId,
  hoveredFeatureId,
  addSource,
  addVectorTileSource,
  addPopup,
  addPopupOnClick,
  closeActivePopup,
  addImage,
  addLayer,
  flyTo,
  fitToDefaultBounds,
  listenToHoveredFeature,
  removeLayer,
  removeSource,
  setData,
  getData,
  setLayoutProperty,
  setPaintProperty,
})
</script>

<style lang="scss">
.maplibregl-popup {
  transition: opacity 0.15s ease-in-out;
  opacity: 0;
  user-select: none;

  &.show {
    opacity: 1;
    .maplibregl-popup-close-button {
      display: inherit;
    }
  }

  &:has(.PlatformMapPopup) .maplibregl-popup-content {
    padding: 0;
    background: transparent;
    box-shadow: none;
  }

  &:has(.PlatformMapPopup) .maplibregl-popup-close-button {
    top: 0.55rem;
    right: 0.55rem;
    border: 1px solid rgb(var(--v-theme-main-blue));
    background-color: #fff;
  }

  --dim-popup-offset: 2rem;
  &.maplibregl-popup-anchor-right .maplibregl-popup-content {
    transform: translateX(calc(-1 * var(--dim-popup-offset)));
  }
  &.maplibregl-popup-anchor-left .maplibregl-popup-content {
    transform: translateX(var(--dim-popup-offset));
  }
  &.maplibregl-popup-anchor-bottom .maplibregl-popup-content {
    transform: translateY(calc(-1 * var(--dim-popup-offset)));
  }
  &.maplibregl-popup-anchor-top .maplibregl-popup-content {
    transform: translateY(var(--dim-popup-offset));
  }
  &.maplibregl-popup-anchor-top-right .maplibregl-popup-content {
    transform: translate(calc(-1 * var(--dim-popup-offset)), var(--dim-popup-offset));
  }
  &.maplibregl-popup-anchor-top-left .maplibregl-popup-content {
    transform: translate(var(--dim-popup-offset), var(--dim-popup-offset));
  }
  &.maplibregl-popup-anchor-bottom-right .maplibregl-popup-content {
    transform: translate(calc(-1 * var(--dim-popup-offset)), calc(-1 * var(--dim-popup-offset)));
  }
  &.maplibregl-popup-anchor-bottom-left .maplibregl-popup-content {
    transform: translate(var(--dim-popup-offset), calc(-1 * var(--dim-popup-offset)));
  }

  .maplibregl-popup-content {
    padding: inherit;
    width: fit-content;
    transform: translateY(-2rem);
    overflow: visible;
  }
  .maplibregl-popup-close-button {
    display: none;
    position: absolute;
    top: -0.5rem;
    right: -0.5rem;
    margin: 0;
    background: white;
    box-shadow: 0 0.25rem 0.25rem -0.125rem rgba(0, 0, 0, 0.15);
    color: black;
    --dim-map-btn-w: 2rem;
    background-image: url(@/assets/images/icons/map/mdi-close.svg);
    background-position: center;
    width: var(--dim-map-btn-w);
    height: var(--dim-map-btn-w);
    border-radius: 50%;
    transition: none;
    z-index: 10;
  }

  .maplibregl-popup-tip {
    display: none;
    pointer-events: none;
  }
}

.UserLayerPopup {
  min-width: 10rem;
  max-width: 18rem;
  padding: 0.125rem;
  font-family: inherit;

  &__title {
    font-weight: 600;
    font-size: 0.8125rem;
    color: rgb(17 24 39);
    margin: 0 0 0.5rem;
    padding-bottom: 0.375rem;
    border-bottom: 1px solid rgb(229 231 235);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.75rem;
  }

  &__key {
    font-weight: 500;
    color: rgb(75 85 99);
    padding: 0.2rem 0.625rem 0.2rem 0;
    vertical-align: top;
    white-space: nowrap;
    max-width: 7rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__val {
    color: rgb(17 24 39);
    word-break: break-word;
    max-width: 10rem;
    padding: 0.2rem 0;
  }

  &__empty {
    color: rgb(107 114 128);
    font-style: italic;
    font-size: 0.75rem;
    margin: 0;
  }
}

.MapToolsPanel__hoverDeletePopup.maplibregl-popup .maplibregl-popup-content {
  padding: 0;
  min-width: 0;
  background: none;
  box-shadow: none;
  transform: none !important;
}

.MapToolsPanel__hoverDeleteWrap {
  display: flex;
  align-items: center;
  justify-content: center;
}

.MapToolsPanel__hoverDeleteBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0.2rem;
  border: none;
  border-radius: 50%;
  background: #1a1a1a;
  color: white;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    filter 0.15s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
}

.MapToolsPanel__hoverDeleteBtn:hover,
.MapToolsPanel__hoverDeleteBtn:focus-visible {
  filter: brightness(1.3);
  transform: scale(1.1);
}

.MapToolsPanel__hoverDeleteBtn:focus-visible {
  outline: 2px solid white;
  outline-offset: 2px;
}

.Map {
  width: 100%;
  height: 100%;
  min-height: 10rem;
  position: relative;

  --map-margin: 1.5rem;

  .maplibregl-ctrl-top-left,
  .maplibregl-ctrl-bottom-right,
  .maplibregl-ctrl-bottom-left,
  .maplibregl-ctrl-top-right {
    margin: var(--map-margin);
    display: flex;
    flex-flow: column nowrap;
  }

  &[small='true'] {
    --map-margin: 0.5rem;
    min-height: 12rem;

    .maplibregl-ctrl-group button {
      --dim-btn-h: 2rem;
      box-sizing: border-box;
      width: var(--dim-btn-h);
      height: var(--dim-btn-h);
      max-height: var(--dim-btn-h);
      min-height: var(--dim-btn-h);
    }

    .maplibregl-ctrl-attrib.maplibregl-compact {
      margin: 0;
    }
  }

  .maplibregl-ctrl-group {
    display: flex;
    flex-flow: column nowrap;
    gap: 0.62rem;
    background: none;
    box-shadow: none;
    margin: 0;
    transition: all 0.15s ease-in;

    button {
      display: flex;
      justify-content: center;
      background: #fff;
      box-shadow: 0 0.25rem 0.25rem -0.125rem rgba(0, 0, 0, 0.15);
      color: var(--color-main);
      --dim-map-btn-w: 2.5rem;
      width: var(--dim-map-btn-w);
      height: var(--dim-map-btn-w);
      border-radius: 50% !important;
      overflow: hidden;
      border-width: 0;
      position: relative;
      &.isActive {
        background: #f0f0f0;
      }

      &.isActive:hover {
        background: #e6e6e6;
      }
      &:hover {
        background: #f0f0f0;
      }

      &.maplibregl-ctrl-zoom-in {
        .maplibregl-ctrl-icon {
          background-image: none !important;
          -webkit-mask-image: url(@/assets/images/icons/map/material-symbols-light--add-2-rounded.svg) !important;
          mask-image: url(@/assets/images/icons/map/material-symbols-light--add-2-rounded.svg) !important;
          -webkit-mask-repeat: no-repeat !important;
          mask-repeat: no-repeat !important;
          -webkit-mask-position: center !important;
          mask-position: center !important;
          -webkit-mask-size: 20px 20px !important;
          mask-size: 20px 20px !important;
          background-color: var(--color-main) !important;
        }
      }

      &.maplibregl-ctrl-zoom-out {
        .maplibregl-ctrl-icon {
          background-image: none !important;
          -webkit-mask-image: url(@/assets/images/icons/map/material-symbols-light--remove-rounded.svg) !important;
          mask-image: url(@/assets/images/icons/map/material-symbols-light--remove-rounded.svg) !important;
          -webkit-mask-repeat: no-repeat !important;
          mask-repeat: no-repeat !important;
          -webkit-mask-position: center !important;
          mask-position: center !important;
          -webkit-mask-size: 20px 20px !important;
          mask-size: 20px 20px !important;
          background-color: var(--color-main) !important;
        }
      }
    }
  }
}

.cursor-crosshair {
  cursor: crosshair !important;
}
</style>
